import React, { useCallback, useState } from 'react';
import { useFloorplansByBedroom } from './hooks/useFloorplansByBedroom';
import { BedroomTabs } from './BedroomTabs';
import { MobileFloorPlanCarousel } from './MobileFloorPlanCarousel';
import { DesktopFloorPlanCarousel } from './DesktopFloorPlanCarousel';
import { UnitDetailOverlay } from './UnitDetailOverlay';
import { UnitDetailPanel } from './UnitDetailOverlay/UnitDetailPanel';
import { SeeAllUnitsOverlay } from './SeeAllUnitsOverlay';
import { buildUnitSpecs } from './UnitDetailOverlay/UnitDetailSpokeContent';
import { useDetailPanel } from '../../../ui/DetailPanel';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

export const UnitsModule = () => {
  const { bedroomOptions, floorplansByBedroom, hasFloorplans } = useFloorplansByBedroom();
  const { openPanel, isMobile } = useDetailPanel();

  const [activeBeds, setActiveBeds] = useState<number | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<{ unit: Unit; floorplan: Floorplan } | null>(null);
  const [seeAllFloorplan, setSeeAllFloorplan] = useState<Floorplan | null>(null);

  const effectiveBeds = activeBeds ?? bedroomOptions[0]?.beds ?? 0;

  const handleTabChange = useCallback((beds: number) => {
    setActiveBeds(beds);
  }, []);

  const handleUnitSelect = useCallback(
    (unit: Unit, floorplan: Floorplan) => {
      if (isMobile) {
        setSelectedUnit({ unit, floorplan });
      } else {
        openPanel('units', UnitDetailPanel, {
          config: { unit, floorplan, title: `Unit ${unit.name}`, subtitle: buildUnitSpecs(unit) },
        });
      }
    },
    [isMobile, openPanel],
  );

  const handleCloseOverlay = useCallback(() => {
    setSelectedUnit(null);
  }, []);

  const handleSeeAll = useCallback((floorplan: Floorplan) => {
    setSeeAllFloorplan(floorplan);
  }, []);

  const handleCloseSeeAll = useCallback(() => {
    setSeeAllFloorplan(null);
  }, []);

  const handleUnitSelectFromSeeAll = useCallback(
    (unit: Unit, floorplan: Floorplan) => {
      setSeeAllFloorplan(null);
      handleUnitSelect(unit, floorplan);
    },
    [handleUnitSelect],
  );

  if (!hasFloorplans) {
    return null;
  }

  return (
    <>
      <BedroomTabs options={bedroomOptions} activeBeds={effectiveBeds} onTabChange={handleTabChange} />

      {bedroomOptions.map((option) => {
        const floorplans = floorplansByBedroom.get(option.beds) ?? [];
        const isActive = option.beds === effectiveBeds;

        if (floorplans.length === 0) return null;

        return (
          <div
            key={option.beds}
            id={`bedroom-tabpanel-${option.beds}`}
            role="tabpanel"
            aria-labelledby={`bedroom-tab-${option.beds}`}
            hidden={!isActive}
          >
            {/* Mobile: Carousel */}
            <div className={styles.carouselContainer}>
              <MobileFloorPlanCarousel
                floorplans={floorplans}
                onUnitSelect={handleUnitSelect}
                onSeeAll={handleSeeAll}
              />
            </div>

            {/* Desktop: Paginated Carousel */}
            <div className={styles.desktopCarouselContainer}>
              <DesktopFloorPlanCarousel
                floorplans={floorplans}
                onUnitSelect={handleUnitSelect}
                onSeeAll={handleSeeAll}
              />
            </div>
          </div>
        );
      })}

      {isMobile && selectedUnit && (
        <UnitDetailOverlay
          unit={selectedUnit.unit}
          floorplan={selectedUnit.floorplan}
          open={true}
          onClose={handleCloseOverlay}
        />
      )}

      {seeAllFloorplan && (
        <SeeAllUnitsOverlay
          floorplan={seeAllFloorplan}
          open={true}
          onClose={handleCloseSeeAll}
          onUnitSelect={handleUnitSelectFromSeeAll}
          isMobile={isMobile}
        />
      )}
    </>
  );
};
